import { motion } from "framer-motion";
import {
  Award,
  BookOpenCheck,
  Target,
  Users,
  Lightbulb,
  FlaskConical,
  Sparkles,
} from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

const activities = [
  {
    label: "a)",
    icon: Users,
    title: "Quality-related Workshops & Seminars",
    description:
      "Inter and intra institutional workshops, seminars on quality related themes and promotion of quality circles.",
  },
  {
    label: "b)",
    icon: FlaskConical,
    title: "Research Awareness & Development",
    description:
      "Workshop and seminar on awareness, motivation, and way of conducting research and report writing.",
  },
  {
    label: "c)",
    icon: Lightbulb,
    title: "Development of Quality Culture",
    description: "Development of Quality Culture in the University.",
  },
  {
    label: "d)",
    icon: Users,
    title: "Stakeholder Engagement",
    description:
      "Meeting with stakeholders: Sharing views and opinions with Alumni, employers, guardians, and students.",
  },
];

const highlights = [
  {
    icon: Award,
    title: "Quality Assurance",
    description: "Enhancing and sustaining higher education standards.",
  },
  {
    icon: Target,
    title: "Continuous Improvement",
    description: "A consistent and systematic approach to institutional growth.",
  },
  {
    icon: BookOpenCheck,
    title: "Academic Excellence",
    description: "Supporting quality learning, training, and research.",
  },
];

export default function AboutIQAC() {
  return (
    <section className="relative isolate overflow-hidden rounded-3xl bg-gradient-to-br from-slate-50 via-white to-cyan-50 px-4 py-10 sm:px-8 lg:px-10">
      {/* Decorative background */}
      <div className="pointer-events-none absolute -right-24 -top-24 -z-10 h-72 w-72 rounded-full bg-cyan-200/30 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-20 -z-10 h-72 w-72 rounded-full bg-blue-200/30 blur-3xl" />

      <div className="mx-auto max-w-6xl">
        {/* Heading */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
          transition={{ duration: 0.65 }}
          className="mb-8"
        >
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-white/80 px-4 py-2 text-sm font-semibold text-[#014864] shadow-sm">
            <Sparkles size={16} className="text-cyan-600" />
            Institutional Quality Assurance Cell
          </div>

          <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            About{" "}
            <span className="bg-gradient-to-r from-[#014864] to-cyan-600 bg-clip-text text-transparent">
              IQAC
            </span>
          </h2>

          <div className="mt-4 h-1.5 w-24 rounded-full bg-gradient-to-r from-[#014864] to-cyan-400" />

          <p className="mt-5 max-w-3xl text-base leading-8 text-slate-600">
            Building a culture of quality, continuous improvement, and academic
            excellence at the University of Creative Technology Chittagong.
          </p>
        </motion.div>

        {/* Highlight cards */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={{
            hidden: {},
            visible: {
              transition: { staggerChildren: 0.12 },
            },
          }}
          className="mb-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          {highlights.map((item, index) => {
            const Icon = item.icon;

            return (
              <motion.div
                key={item.title}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                whileHover={{ y: -6 }}
                className="group rounded-2xl border border-white bg-white/90 p-5 shadow-sm transition-shadow duration-300 hover:shadow-xl hover:shadow-cyan-900/10"
              >
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#014864] to-cyan-600 text-white shadow-md transition-transform duration-300 group-hover:rotate-3 group-hover:scale-110">
                  <Icon size={23} />
                </div>

                <h3 className="text-lg font-bold text-slate-800">
                  {item.title}
                </h3>

                <p className="mt-2 text-sm leading-6 text-slate-500">
                  {item.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* About content */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative mb-10 overflow-hidden rounded-2xl border border-cyan-100 bg-white p-6 shadow-sm sm:p-8"
        >
          <div className="absolute left-0 top-0 h-full w-1.5 bg-gradient-to-b from-[#014864] to-cyan-400" />

          <div className="mb-5 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-cyan-50 text-[#014864]">
              <BookOpenCheck size={22} />
            </div>

            <h3 className="text-xl font-bold text-slate-900">
              Our Commitment to Quality
            </h3>
          </div>

          <div className="space-y-4 text-justify text-sm leading-8 text-slate-600 sm:text-base">
            <p>
              Institutional Quality Assurance Cell (IQAC) is an essential part
              of a university to ensure quality of learning and training
              programs in higher education. In order to maintain the quality
              of higher education, it was first adopted in 1900 in the United
              States of America. As a second country Japan adopted this method
              in 1946. Then other countries of the world started following this
              process.
            </p>

            <p>
              The present government headed by the Hon’ble Prime Minister
              Sheikh Hasina is committed to change the landscape of higher
              education through enhancement of quality of higher education
              and research in the country. For achieving the target of quality
              education, the Government of Bangladesh (GoB) and the World Bank
              has generously funded the project which is known as Higher
              Education Quality Enhancement Project (HEQEP). The project is
              being implemented by the University Grants Commission, with help
              of the project officials and that of by the World Bank.
            </p>

            <p>
              The IQAC of UCTC was established in July 2020 in order to ensure
              quality education at the tertiary level and aims to meet the
              demands of contemporary times. Since quality enhancement is a
              continuous process, the IQAC, UCTC is trying to become a part of
              an institution&apos;s system and work towards realizing the goals
              of quality enhancement and sustenance.
            </p>

            <p>
              The prime aim of the IQAC of UCTC is to develop a system for
              conscious, consistent and catalytic improvement in the
              performance of institutions. Moreover, IQAC is trying to
              identify the gaps between the existing system and stakeholder
              demands and to enhance and maintain the standards of quality of
              higher education up to the international standards.
            </p>

            <p>
              At present, UCTC-IQAC is working to assess all 6 departments
              academic program to find out the major strength, opportunity,
              and weakness. For instance, IQAC has organized the following
              programs.
            </p>
          </div>
        </motion.div>

        {/* Activities */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={{
            hidden: {},
            visible: {
              transition: { staggerChildren: 0.12 },
            },
          }}
        >
          <div className="mb-6">
            <span className="text-sm font-bold uppercase tracking-[0.18em] text-cyan-700">
              What We Do
            </span>

            <h3 className="mt-2 text-2xl font-extrabold text-slate-900 sm:text-3xl">
              IQAC Activities & Initiatives
            </h3>

            <p className="mt-3 max-w-2xl leading-7 text-slate-500">
              Initiatives designed to promote quality assurance, research,
              institutional development, and stakeholder collaboration.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {activities.map((activity, index) => {
              const Icon = activity.icon;

              return (
                <motion.div
                  key={activity.label}
                  variants={fadeUp}
                  transition={{ duration: 0.5 }}
                  whileHover={{ y: -5, scale: 1.01 }}
                  className="group relative overflow-hidden rounded-2xl border border-cyan-100 bg-gradient-to-br from-white to-cyan-50/80 p-5 shadow-sm transition-all duration-300 hover:border-cyan-300 hover:shadow-xl hover:shadow-cyan-900/10 sm:p-6"
                >
                  <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-cyan-100/50 transition-transform duration-500 group-hover:scale-150" />

                  <div className="relative flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-[#014864] text-white shadow-md transition-all duration-300 group-hover:bg-cyan-600 group-hover:rotate-3">
                      <Icon size={22} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="mb-2 flex items-center gap-2">
                        <span className="text-sm font-extrabold text-amber-500">
                          {activity.label}
                        </span>

                        <h4 className="font-bold text-slate-800">
                          {activity.title}
                        </h4>
                      </div>

                      <p className="text-sm leading-7 text-slate-600">
                        {activity.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
}